import { useEffect, useState, type CSSProperties } from 'react'
import { ColorPicker, Select, message } from 'antd'
import { BgColorsOutlined } from '@ant-design/icons'
import type { UploadFile } from 'antd'
import type { AggregationColor } from 'antd/es/color-picker/color'
import { Button } from '@/components/ui/Button'
import { ImageUploadWithPreview } from '@/components/ui/ImageUpload'
import { api } from '@/lib/apiClient'
import { BADGE_FONT_FAMILIES } from '@/lib/badgeTemplate'

export type LuckyDrawAppearance = {
  backgroundColor?: string | null
  backgroundPosition?: string | null
  fontFamily?: string | null
  fontColor?: string | null
  backgroundImageUrl?: string | null
}

type SaveResponse =
  | { success: true; appearance: LuckyDrawAppearance; backgroundImageUrl: string | null }
  | { success: false; errors: string[] }

type Props = {
  eventSlug: string
  appearance: LuckyDrawAppearance
  onAppearanceChange?: (appearance: LuckyDrawAppearance) => void
}

const FONT_FAMILY_OPTIONS = BADGE_FONT_FAMILIES.map((family) => ({
  label: family,
  value: family,
}))

const BACKGROUND_POSITION_OPTIONS: { label: string; value: string }[] = [
  { label: 'Center', value: 'center' },
  { label: 'Top', value: 'top' },
  { label: 'Bottom', value: 'bottom' },
  { label: 'Left', value: 'left' },
  { label: 'Right', value: 'right' },
  { label: 'Top Left', value: 'top left' },
  { label: 'Top Right', value: 'top right' },
  { label: 'Bottom Left', value: 'bottom left' },
  { label: 'Bottom Right', value: 'bottom right' },
]

const DEFAULT_BACKGROUND_POSITION = 'center'

const PREVIEW_BACKGROUND_FALLBACK = '#00FF00'

// Cleared appearance fields are persisted as empty strings, which `??` does not
// treat as missing. Normalise them to null so the defaults kick in.
const presence = (value?: string | null) => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

// Attachment URLs carry the original filename, which Rails leaves unescaped for
// characters like ( ) and '. An unquoted CSS url() silently drops those, so the
// value must be quoted and any quote/backslash escaped.
const cssUrl = (url: string) => `url("${url.replace(/["\\]/g, '\\$&')}")`

const LuckyDrawAppearanceSettings = ({ eventSlug, appearance, onAppearanceChange }: Props) => {
  const [backgroundColor, setBackgroundColor] = useState<string | null>(
    presence(appearance.backgroundColor)
  )
  const [backgroundPosition, setBackgroundPosition] = useState<string>(
    presence(appearance.backgroundPosition) || DEFAULT_BACKGROUND_POSITION
  )
  const [fontFamily, setFontFamily] = useState<string | null>(presence(appearance.fontFamily))
  const [fontColor, setFontColor] = useState<string | null>(presence(appearance.fontColor))
  const [backgroundImageUrl, setBackgroundImageUrl] = useState<string | null>(
    presence(appearance.backgroundImageUrl)
  )
  const [imageFile, setImageFile] = useState<UploadFile[]>([])
  const [removeImage, setRemoveImage] = useState(false)
  const [saving, setSaving] = useState(false)

  // Object URL for the locally-picked-but-unsaved file, so the preview matches
  // what the user sees in the Background Image panel before saving.
  const selectedFile = imageFile[0]?.originFileObj ?? null
  const [localImageUrl, setLocalImageUrl] = useState<string | null>(null)

  // Create and revoke the object URL in the same effect run so the preview can
  // never render a URL that has already been revoked.
  useEffect(() => {
    if (!selectedFile) {
      setLocalImageUrl(null)
      return
    }

    const objectUrl = URL.createObjectURL(selectedFile)
    setLocalImageUrl(objectUrl)

    return () => {
      URL.revokeObjectURL(objectUrl)
      setLocalImageUrl(null)
    }
  }, [selectedFile])

  const handleImageChange = (fileList: UploadFile[]) => {
    setImageFile(fileList)
    setRemoveImage(false)
  }

  const handleImageRemove = () => {
    setImageFile([])
    setBackgroundImageUrl(null)
    setRemoveImage(true)
  }

  const handleSave = async () => {
    setSaving(true)

    const formData = new FormData()
    formData.append('appearance[backgroundColor]', backgroundColor ?? '')
    formData.append('appearance[backgroundPosition]', backgroundPosition ?? '')
    formData.append('appearance[fontFamily]', fontFamily ?? '')
    formData.append('appearance[fontColor]', fontColor ?? '')

    if (removeImage) {
      formData.append('remove_lucky_draw_background_image', 'true')
    } else if (imageFile.length > 0 && imageFile[0].originFileObj) {
      formData.append('lucky_draw_background_image', imageFile[0].originFileObj)
    }

    try {
      const data = await api.patch<SaveResponse>(
        `/events/${eventSlug}/lucky_draw/appearance`,
        formData
      )

      if (data.success) {
        const next: LuckyDrawAppearance = {
          backgroundColor: presence(data.appearance.backgroundColor),
          backgroundPosition: presence(data.appearance.backgroundPosition),
          fontFamily: presence(data.appearance.fontFamily),
          fontColor: presence(data.appearance.fontColor),
          backgroundImageUrl: presence(data.backgroundImageUrl),
        }
        setBackgroundColor(next.backgroundColor ?? null)
        setBackgroundPosition(next.backgroundPosition || DEFAULT_BACKGROUND_POSITION)
        setFontFamily(next.fontFamily ?? null)
        setFontColor(next.fontColor ?? null)
        setBackgroundImageUrl(next.backgroundImageUrl ?? null)
        setImageFile([])
        setRemoveImage(false)
        onAppearanceChange?.(next)
        message.success('Appearance saved')
      } else {
        message.error(data.errors?.[0] ?? 'Failed to save appearance')
      }
    } catch (error) {
      console.error('Error saving lucky draw appearance:', error)
      message.error('Failed to save appearance')
    } finally {
      setSaving(false)
    }
  }

  // Resolve the preview background with the SAME priority as the real display
  // (see lucky_draw_screen/show.page.tsx): locally-picked image > saved image
  // url > background colour > #00FF00 fallback.
  const previewImageUrl = localImageUrl ?? backgroundImageUrl
  const previewBackground: CSSProperties = previewImageUrl
    ? {
        backgroundImage: cssUrl(previewImageUrl),
        backgroundSize: 'cover',
        backgroundPosition: backgroundPosition || DEFAULT_BACKGROUND_POSITION,
        backgroundRepeat: 'no-repeat',
      }
    : { background: backgroundColor ?? PREVIEW_BACKGROUND_FALLBACK }

  return (
    <div className="p-6">
      <div className="bg-white border border-[#EBEBEB] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-8 py-5 bg-white border-b border-[#EBEBEB] flex items-center gap-3">
          <BgColorsOutlined className="text-xl text-black/85" />
          <span className="text-xl font-normal leading-6 text-black/85">
            Big Screen Appearance
          </span>
          <div className="ml-auto">
            <Button variant="primary" onClick={handleSave} loading={saving}>
              Save
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Controls */}
          <div className="flex flex-col gap-6">
            <ImageUploadWithPreview
              label="Background Image"
              fileList={imageFile}
              onChange={handleImageChange}
              onRemove={handleImageRemove}
              previewUrl={backgroundImageUrl}
              previewAlt="Lucky draw background"
              aspectRatio="16:9"
              maxSizeMB={5}
              helpText="Recommended: a wide KV/background image. Maximum upload file size: 5 MB."
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Background Colour
              </label>
              <div className="flex items-center gap-3">
                <ColorPicker
                  value={backgroundColor ?? undefined}
                  allowClear
                  format="hex"
                  onChangeComplete={(color: AggregationColor) =>
                    setBackgroundColor(color.toHexString())
                  }
                  onClear={() => setBackgroundColor(null)}
                  showText
                />
                {backgroundColor && (
                  <button
                    type="button"
                    onClick={() => setBackgroundColor(null)}
                    className="text-sm font-semibold text-[#4277BB] hover:text-[#4277BB]/80 bg-transparent border-none cursor-pointer"
                  >
                    Clear
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Used when no background image is set.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Background Position
              </label>
              <Select
                style={{ width: '100%' }}
                value={backgroundPosition}
                onChange={(value) => setBackgroundPosition(value)}
                options={BACKGROUND_POSITION_OPTIONS}
              />
              <p className="text-xs text-gray-500 mt-1">
                Adjusts which part of the background image is visible.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Font Family</label>
              <Select
                style={{ width: '100%' }}
                placeholder="Default font"
                allowClear
                value={fontFamily ?? undefined}
                onChange={(value) => setFontFamily(value ?? null)}
                options={FONT_FAMILY_OPTIONS}
              />
              <p className="text-xs text-gray-500 mt-1">
                Applied to the title and winner names on the big screen.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Font Colour</label>
              <div className="flex items-center gap-3">
                <ColorPicker
                  value={fontColor ?? undefined}
                  allowClear
                  format="hex"
                  onChangeComplete={(color: AggregationColor) =>
                    setFontColor(color.toHexString())
                  }
                  onClear={() => setFontColor(null)}
                  showText
                />
                {fontColor && (
                  <button
                    type="button"
                    onClick={() => setFontColor(null)}
                    className="text-sm font-semibold text-[#4277BB] hover:text-[#4277BB]/80 bg-transparent border-none cursor-pointer"
                  >
                    Clear
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Applied to the title and winner names. Leave empty to keep the default colours.
              </p>
            </div>
          </div>

          {/* Live Preview — mirrors the real big screen (dark bar + KV + circle) */}
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-2">Preview</span>
            <div className="rounded-lg overflow-hidden border border-gray-200 aspect-video flex flex-col">
              {/* Dark top bar with centered title (scaled down to fit) */}
              <div className="shrink-0 w-full bg-dark flex justify-center py-1.5">
                <p
                  className="text-base font-extrabold uppercase m-0 leading-none"
                  style={{
                    fontFamily: fontFamily ?? undefined,
                    color: fontColor ?? '#1e3a8a',
                  }}
                >
                  Lucky Draw
                </p>
              </div>

              {/* KV background region filling the rest with the centered dark circle */}
              <div
                className="flex-1 min-h-0 w-full flex items-center justify-center overflow-hidden"
                style={previewBackground}
              >
                <div className="bg-dark text-white rounded-full aspect-square w-1/2 max-w-[120px] flex items-center justify-center text-center px-2">
                  <p
                    className="text-xs font-semibold uppercase m-0 leading-tight"
                    style={{
                      fontFamily: fontFamily ?? undefined,
                      color: fontColor ?? '#ffffff',
                    }}
                  >
                    Are You Ready?
                  </p>
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Background image takes priority over background colour.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LuckyDrawAppearanceSettings
